import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _isEqualWith from 'lodash/isEqualWith';
import { WindowWidthContext, useClickOutside, helpers } from '@minecraft.utils';
import { FilterValues, FilterProps } from '../shared/types';
import {
  useFilterPills,
  isEqualCustomizer,
  shouldClearButtonBeVisible,
  getClearedFilter,
  saveFilterUtilities,
} from '../shared/utils';
import { FilterOverlayContainer, FilterOverlayWrapper } from './FilterOverlayStyles';
import FilterOverlayButton from './FilterOverlayButton';
import FilterOverlayForm from './FilterOverlayForm';
import FilterOverlayBreadbox from './FilterOverlayBreadbox';

const { changeBodyOverflow } = helpers;

const FilterOverlay = <Values,>({
  showAdvancedSearchButton,
  showBreadbox = true,
  advancedSearchRoute,
  searchButtonLabel,
  sessionStorageKey,
  filtersLabel,
  excludeValuesFromBreadbox,
  initialFilters,
  appliedFilters,
  onFiltersChange,
  children,
  className,
}: FilterProps<Values>): JSX.Element => {
  const { lessSm } = WindowWidthContext.useWidthData();

  const filterOverlayRef = useRef(null);
  const formikRef = useRef(null);

  const [isFilterOverlayOpened, setFilterOverlayOpened] = useState(false);
  const [isFilterOverlayFormDirty, setFilterOverlayFormDirty] = useState(false);
  const [retainedFilters, setRetainedFilters] = useState({
    ...initialFilters,
    ...appliedFilters,
  });

  const pills = useFilterPills({ filtersLabel, filters: appliedFilters, excludeValues: excludeValuesFromBreadbox });

  const isClearButtonVisible = useMemo(
    () => shouldClearButtonBeVisible({ pills, appliedFilters, retainedFilters, initialFilters }),
    [pills, appliedFilters, retainedFilters, initialFilters]
  );

  const toggleFilterOverlay = useCallback((): void => {
    setFilterOverlayOpened(!isFilterOverlayOpened);
  }, [isFilterOverlayOpened]);

  const handleFilterOverlayFormDirtyChange = useCallback(
    (newFilters: FilterValues): void => {
      if (_isEqualWith(appliedFilters, newFilters, isEqualCustomizer)) {
        setFilterOverlayFormDirty(false);

        return;
      }

      if (
        !_isEqualWith(retainedFilters, newFilters, isEqualCustomizer) ||
        !_isEqualWith(appliedFilters, newFilters, isEqualCustomizer)
      ) {
        setFilterOverlayFormDirty(true);

        return;
      }

      setFilterOverlayFormDirty(false);
    },
    [appliedFilters, retainedFilters]
  );

  const handleRetainedFiltersChange = useCallback(
    (newFilters: FilterValues): void => {
      handleFilterOverlayFormDirtyChange(newFilters);

      setRetainedFilters(newFilters);
    },
    [handleFilterOverlayFormDirtyChange]
  );

  const handleClear = useCallback(
    (filterKey?: string): void => {
      saveFilterUtilities.clearSavedFiltersForThisSession({ storageKey: sessionStorageKey });
      onFiltersChange({
        ...retainedFilters,
        ...(filterKey ? getClearedFilter({ filterKey, initialFilters }) : initialFilters),
      });
    },
    [retainedFilters]
  );

  const handleRetainedFiltersClear = useCallback((): void => {
    if (!_isEqualWith(appliedFilters, initialFilters, isEqualCustomizer)) {
      saveFilterUtilities.clearSavedFiltersForThisSession({ storageKey: sessionStorageKey });
      onFiltersChange(initialFilters);
    }

    formikRef.current?.setValues && formikRef.current.setValues(initialFilters);
  }, [appliedFilters]);

  useClickOutside(filterOverlayRef, () => {
    setFilterOverlayOpened(false);
  });

  useEffect(() => {
    setRetainedFilters(
      (prevRetainedFilters: FilterValues): FilterValues => ({
        ...prevRetainedFilters,
        ...appliedFilters,
      })
    );
    setFilterOverlayFormDirty(false);
  }, [appliedFilters]);

  useEffect(() => {
    changeBodyOverflow(lessSm && isFilterOverlayOpened ? 'hidden' : 'auto');

    return (): void => changeBodyOverflow('auto');
  }, [isFilterOverlayOpened, lessSm]);

  return (
    <FilterOverlayContainer className={className}>
      <FilterOverlayWrapper ref={filterOverlayRef}>
        <FilterOverlayButton
          areFiltersApplied={!showBreadbox && !_isEmpty(pills)}
          isFilterOverlayOpened={isFilterOverlayOpened}
          isFilterOverlayFormDirty={isFilterOverlayFormDirty}
          toggleFilterOverlay={toggleFilterOverlay}
        />
        {isFilterOverlayOpened && (
          <FilterOverlayForm
            isClearButtonVisible={isClearButtonVisible}
            showAdvancedSearchButton={showAdvancedSearchButton}
            advancedSearchRoute={advancedSearchRoute}
            searchButtonLabel={searchButtonLabel}
            sessionStorageKey={sessionStorageKey}
            formikRef={formikRef}
            retainedFilters={retainedFilters}
            toggleFilterOverlay={toggleFilterOverlay}
            onClear={handleRetainedFiltersClear}
            onRetainedFiltersChange={handleRetainedFiltersChange}
            onFiltersChange={onFiltersChange}
          >
            {children()}
          </FilterOverlayForm>
        )}
      </FilterOverlayWrapper>
      {showBreadbox && !lessSm && !_isEmpty(pills) && <FilterOverlayBreadbox pills={pills} onClear={handleClear} />}
    </FilterOverlayContainer>
  );
};

export default FilterOverlay;
